import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { apiService } from '@signals/app-shell/services/api';

/** Pathfinder Monitor MQTT QR — short-lived provision token (no MQTT password in QR). */
export interface PhoneTrackerMqttQrPayload {
  provision_v: number;
  api_base: string;
  token: string;
  expires_in: number;
}

/** Stock Network Survey MQTT QR JSON (password included — app requirement). */
export interface NetworkSurveyMqttQrPayload {
  mqtt_host: string;
  mqtt_port: number;
  mqtt_tls: boolean;
  mqtt_username: string;
  mqtt_password: string;
  mqtt_topic_prefix: string;
  mqtt_qos: number;
  cellular_stream_enabled: boolean;
  wifi_stream_enabled: boolean;
  bluetooth_stream_enabled: boolean;
  gnss_stream_enabled: boolean;
  device_status_stream_enabled: boolean;
  phone_state_stream_enabled: boolean;
}

export type MqttQrAppTarget = 'pathfinder_monitor' | 'network_survey';

interface MqttSetupQrDialogProps {
  open: boolean;
  onClose: () => void;
  /** Optional; defaults to Pathfinder Monitor. */
  initialApp?: MqttQrAppTarget;
  /** Bubble load errors to parent (e.g. snackbar). */
  onError?: (message: string) => void;
}

const MqttSetupQrDialog: React.FC<MqttSetupQrDialogProps> = ({
  open,
  onClose,
  initialApp = 'pathfinder_monitor',
  onError,
}) => {
  const [app, setApp] = useState<MqttQrAppTarget>(initialApp);
  const [monitorPayload, setMonitorPayload] = useState<PhoneTrackerMqttQrPayload | null>(null);
  const [nsPayload, setNsPayload] = useState<NetworkSurveyMqttQrPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  // Keep parent onError out of effect deps — inline handlers were retriggering
  // loadPayload forever (spinner flash / never settle).
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const requestGen = useRef(0);

  const loadPayload = useCallback(async (target: MqttQrAppTarget) => {
    const gen = ++requestGen.current;
    setLoading(true);
    setLocalError(null);
    try {
      if (target === 'pathfinder_monitor') {
        const resp = await apiService.client.get<PhoneTrackerMqttQrPayload>('/mqtt/qr-payload');
        if (gen !== requestGen.current) return;
        setMonitorPayload(resp.data);
      } else {
        const resp = await apiService.client.get<NetworkSurveyMqttQrPayload>(
          '/mqtt/ns-qr-payload',
        );
        if (gen !== requestGen.current) return;
        setNsPayload(resp.data);
      }
    } catch (e: any) {
      if (gen !== requestGen.current) return;
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let message: string;
      if (status === 403 || status === 401) {
        message = 'Admin access required to show the MQTT QR. Open Settings → MQTT.';
      } else if (typeof detail === 'string') {
        message = detail;
      } else {
        message =
          target === 'pathfinder_monitor'
            ? 'Failed to mint MQTT QR token. Save MQTT settings with a password first.'
            : 'Failed to build Network Survey QR. Save MQTT settings with a password first.';
      }
      setLocalError(message);
      onErrorRef.current?.(message);
      if (target === 'pathfinder_monitor') setMonitorPayload(null);
      else setNsPayload(null);
    } finally {
      if (gen === requestGen.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      requestGen.current += 1;
      setMonitorPayload(null);
      setNsPayload(null);
      setLocalError(null);
      setSecondsLeft(null);
      setLoading(false);
      return;
    }
    setApp(initialApp);
    void loadPayload(initialApp);
    // loadPayload is stable (empty deps); do not depend on onError.
  }, [open, initialApp, loadPayload]);

  useEffect(() => {
    if (!open || app !== 'pathfinder_monitor' || !monitorPayload) {
      setSecondsLeft(null);
      return;
    }
    const endsAt = Date.now() + (monitorPayload.expires_in || 300) * 1000;
    const tick = () => {
      setSecondsLeft(Math.max(0, Math.ceil((endsAt - Date.now()) / 1000)));
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [open, app, monitorPayload]);

  const handleAppChange = (
    _: React.MouseEvent<HTMLElement>,
    next: MqttQrAppTarget | null,
  ) => {
    if (!next || next === app) return;
    setApp(next);
    void loadPayload(next);
  };

  const monitorExpired = app === 'pathfinder_monitor' && secondsLeft === 0;

  const qrPayloadJson = useMemo(() => {
    if (app === 'pathfinder_monitor') {
      return monitorPayload && !monitorExpired ? JSON.stringify(monitorPayload) : null;
    }
    return nsPayload ? JSON.stringify(nsPayload) : null;
  }, [app, monitorPayload, nsPayload, monitorExpired]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>MQTT Setup QR</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Choose which app to configure, then scan the QR on the device.
        </Typography>

        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          color="primary"
          value={app}
          onChange={handleAppChange}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="pathfinder_monitor">Pathfinder Monitor</ToggleButton>
          <ToggleButton value="network_survey">Network Survey</ToggleButton>
        </ToggleButtonGroup>

        {app === 'pathfinder_monitor' ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Pathfinder Monitor → Settings → MQTT → Scan QR Code. One-time token only — the
              phone fetches credentials from Pathfinder (password never in the QR).
            </Typography>
            <Typography
              variant="body2"
              sx={{ mb: 2, fontWeight: 600, color: monitorExpired ? 'error.main' : 'warning.main' }}
            >
              {monitorExpired
                ? 'Expired — switch apps or close and open again.'
                : `Expires in ${secondsLeft ?? '…'}s (single use)`}
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Network Survey → Menu → MQTT Broker Connection → Auto Configure With QR Code.
              Topic prefix is set to <code>networksurvey/</code>. Register the device serial in
              Device Reference (Phone / Watch / Vehicle / Wardriver) so Pathfinder types it
              correctly.
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This QR includes the MQTT password (Network Survey requirement). Keep it private
              and close when done.
            </Alert>
          </>
        )}

        {localError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {localError}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {!loading && qrPayloadJson && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              sx={{
                p: 2,
                bgcolor: 'common.white',
                borderRadius: 1,
                lineHeight: 0,
              }}
            >
              <QRCodeSVG value={qrPayloadJson} size={256} level="M" includeMargin />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {app === 'pathfinder_monitor' && monitorExpired && (
          <Button onClick={() => void loadPayload('pathfinder_monitor')} disabled={loading}>
            Refresh token
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MqttSetupQrDialog;
